import React, { useEffect } from "react";
import { Navigation, NavigationOff } from "lucide-react";
import useTspGpsTracker from "./useTspGpsTracker";
import { GPS_PHASE_UI, formatAccuracy, timeAgo } from "./trackingModel";

// The compact GPS button + its status: this device sharing its location for
// one journey. Used on the journey page and on its Live Location page.
//
//   journeyId  the journey
//   status     GET /tracking data ({ can_share, me, config }) or null while loading
//   onChange   called after GPS is switched on/off (the caller refreshes its status)
//
// The status shown is this device's REAL tracker phase (gpsTracker.js); nothing
// here claims "GPS ON" unless positions are actually arriving.
export default function JourneyGpsControl({ journeyId, status, onChange, compact = false }) {
  const { state, tracker } = useTspGpsTracker();
  const id = String(journeyId);
  const mine = state.journeyId === id;
  const otherJourney = !mine && tracker.isTracking(state.journeyId);
  const phase = mine ? state.phase : "off";
  const ui = GPS_PHASE_UI[phase] || GPS_PHASE_UI.off;
  const canShare = Boolean(status?.can_share);

  // Opening the journey again (e.g. after a reload) while the server still has
  // this user's tracking ON for it: resume, or show "paused".
  const meStatus = status?.me?.status;
  useEffect(() => {
    if (status && canShare) tracker.resumeIfActive(id, status.me, status.config);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the server's view of "me" changes
  }, [id, canShare, meStatus]);

  // The server ended tracking (journey completed / cancelled, or switched off
  // elsewhere): refresh the caller's status so the button reflects it at once.
  useEffect(() => {
    if (mine && (phase === "ended" || phase === "error")) onChange?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per phase change
  }, [mine, phase]);

  const toggle = async () => {
    if (ui.on) await tracker.stop();
    else tracker.start(id, status?.config);
    onChange?.();
  };

  const disabled = !status || (!ui.on && !canShare && phase !== "paused");
  const accuracy = mine && ["on", "offline"].includes(phase) ? formatAccuracy(state.lastFix?.accuracy) : null;
  const details = [];
  if (accuracy) details.push(`Accuracy ${accuracy}`);
  // Browsers report positions only when they change: an old last fix with no
  // error means the device is standing still, not that GPS is lost.
  if (mine && phase === "on" && state.lastFix && Date.now() - state.lastFix.t > 60000) details.push(`Not moving · last fix ${timeAgo(state.lastFix.t)}`);
  if (mine && ui.on && state.lastSentAt) details.push(`Sent ${timeAgo(state.lastSentAt)}`);
  if (mine && state.pending > 1 && phase === "offline") details.push(`${state.pending} locations waiting`);

  let note = mine ? state.message : "";
  if (!note && status && !canShare && !ui.on) note = "GPS tracking is available while the journey is in progress.";
  if (!note && otherJourney) note = "This device is tracking another journey. Enabling GPS here switches tracking to this journey.";

  return (
    <div className={`tsp-gps-control${compact ? " is-compact" : ""}`} data-phase={phase}>
      <button
        type="button"
        className={`tsp-btn tsp-gps-toggle ${ui.on ? "is-on" : ""}`}
        onClick={toggle}
        disabled={disabled}
        aria-pressed={ui.on}
        title={ui.on ? "Stop sharing this device's location" : "Share this device's live location for this journey"}
      >
        {ui.on ? <NavigationOff size={15} aria-hidden="true" /> : <Navigation size={15} aria-hidden="true" />}
        {ui.on ? "Disable GPS" : phase === "paused" ? "Resume GPS" : "Enable GPS"}
      </button>
      <div className="tsp-gps-status" role="status" aria-live="polite">
        <span className={`tsp-gps-badge tone-${ui.tone}`}>
          <span className="tsp-gps-dot" aria-hidden="true" />
          {ui.label}
        </span>
        {details.length > 0 && <span className="tsp-gps-details">{details.join(" · ")}</span>}
        {note && <span className="tsp-gps-note">{note}</span>}
        {phase === "paused" && (
          <button type="button" className="tsp-link-btn tsp-gps-note" onClick={() => tracker.stop().then(() => onChange?.())}>
            Turn tracking off
          </button>
        )}
      </div>
    </div>
  );
}
